import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  externalConnectionControllerActivateConnection,
  externalConnectionControllerCreateSessionToken,
  externalConnectionControllerDeleteConnection,
  externalConnectionControllerGetGroupConnections,
  externalConnectionControllerGetUserConnections,
  externalConnectionControllerTriggerSync,
} from "../lib/gen-api";
import { connectNangoProvider } from "../lib/nango-client";
import {
  GOOGLE_DRIVE_NANGO_INTEGRATION_ID,
  SHAREPOINT_NANGO_INTEGRATION_ID,
} from "../lib/nango-integrations.constants";

/** Providers wired for Nango connect-session + frontend OAuth in this hook */
type NangoConnectableProvider = "GOOGLE_DRIVE" | "SHAREPOINT";

export function useExternalConnectionsData(groupUuid: string | null) {
  const queryClient = useQueryClient();

  const { data: connections, isLoading } = useQuery({
    queryKey: ["external-connections", groupUuid],
    initialData: [],
    queryFn: async () => {
      if (!groupUuid) {
        return [];
      }
      return externalConnectionControllerGetGroupConnections({
        path: {
          groupUuid,
        },
      }).then((r) => r.data?.data ?? []);
    },
    enabled: !!groupUuid,
  });

  const { data: userConnections, isLoading: isUserConnectionsLoading } =
    useQuery({
      queryKey: ["external-connections/user"],
      initialData: [],
      queryFn: async () => {
        return externalConnectionControllerGetUserConnections().then(
          (r) => r.data?.data ?? [],
        );
      },
      enabled: !groupUuid,
    });

  const initiateConnectionMutation = useMutation({
    mutationFn: async (provider: NangoConnectableProvider) => {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      const sessionResponse =
        await externalConnectionControllerCreateSessionToken({
          body: {
            groupUuid,
            provider,
          },
        });

      if (!sessionResponse.data) {
        throw new Error("Failed to create session token");
      }

      const nangoIntegrationId =
        provider === "SHAREPOINT"
          ? SHAREPOINT_NANGO_INTEGRATION_ID
          : GOOGLE_DRIVE_NANGO_INTEGRATION_ID;
      const result = await connectNangoProvider(
        sessionResponse.data.token,
        nangoIntegrationId,
        {
          sharePointConnectionConfig:
            provider === "SHAREPOINT"
              ? sessionResponse.data.sharepoint_connection_config
              : undefined,
        },
      );

      await externalConnectionControllerActivateConnection({
        body: {
          connectionId: result.connectionId,
          groupUuid,
          provider,
        },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["external-connections", groupUuid],
      });
    },
  });

  const deleteConnectionMutation = useMutation({
    mutationFn: async (connectionUuid: string) => {
      await externalConnectionControllerDeleteConnection({
        path: { connectionUuid },
      });
    },
    onSuccess: () => {
      if (groupUuid) {
        queryClient.invalidateQueries({
          queryKey: ["external-connections", groupUuid],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["external-connections/user"],
        });
      }
    },
  });

  const triggerSyncMutation = useMutation({
    mutationFn: async ({
      connectionUuid,
      syncName,
    }: {
      connectionUuid: string;
      syncName: string;
    }) => {
      const response = await externalConnectionControllerTriggerSync({
        path: { connectionUuid },
        body: { syncName },
      });
      return response.data;
    },
    onSuccess: () => {
      if (groupUuid) {
        queryClient.invalidateQueries({
          queryKey: ["external-connections", groupUuid],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["external-connections/user"],
        });
      }
    },
  });

  const initiateConnection = (provider: NangoConnectableProvider) =>
    initiateConnectionMutation.mutateAsync(provider);

  const deleteConnection = async (connectionUuid: string) => {
    return deleteConnectionMutation.mutateAsync(connectionUuid);
  };

  const triggerSync = async (connectionUuid: string, syncName: string) => {
    return triggerSyncMutation.mutateAsync({ connectionUuid, syncName });
  };

  return {
    connections: groupUuid ? connections : userConnections,
    isLoading: groupUuid ? isLoading : isUserConnectionsLoading,
    initiateConnection,
    deleteConnection,
    triggerSync,
    isInitiating: initiateConnectionMutation.isPending,
    isDeleting: deleteConnectionMutation.isPending,
    isSyncing: triggerSyncMutation.isPending,
  };
}
